import React, { useState } from 'react';
import { View, StyleSheet, TouchableOpacity, Platform } from 'react-native';
import { IconButton, Portal, Modal, Text, useTheme, Surface } from 'react-native-paper';
import { MaterialCommunityIcons } from '@expo/vector-icons';

export interface HelpTooltipProps {
  /**
   * Unique help key to fetch content from help.json
   */
  helpKey: string;
  
  /**
   * Help content object with title and description
   */
  content?: {
    title: string;
    description: string;
  };
  
  /**
   * Icon size (default: 20)
   */
  iconSize?: number;
  
  /**
   * Icon color (defaults to theme color)
   */
  iconColor?: string;
  
  /**
   * Position of the icon (default: 'inline')
   */
  position?: 'inline' | 'absolute-top-right';
  
  /**
   * Test ID for testing
   */
  testID?: string;
}

/**
 * HelpTooltip component displays a help icon that opens a modal with help content.
 * 
 * Usage:
 * ```tsx
 * <HelpTooltip helpKey="invite_driver" />
 * 
 * // Or with custom content
 * <HelpTooltip
 *   content={{
 *     title: "Convidar Motorista",
 *     description: "Envie um convite por email..."
 *   }}
 * />
 * ```
 */
export const HelpTooltip: React.FC<HelpTooltipProps> = ({
  helpKey,
  content,
  iconSize = 20,
  iconColor,
  position = 'inline',
  testID = `help-tooltip-${helpKey}`,
}) => {
  const theme = useTheme();
  const [visible, setVisible] = useState(false);

  const showModal = () => setVisible(true);
  const hideModal = () => setVisible(false);

  // If no content provided, try to load from help.json (would need i18n integration)
  // For now, we'll use content prop directly
  const helpContent = content || {
    title: 'Ajuda',
    description: 'Conteúdo de ajuda não disponível para esta secção.',
  };

  const iconColorValue = iconColor || theme.colors.primary;

  return (
    <View style={[styles.container, position === 'absolute-top-right' && styles.absoluteTopRight]}>
      <TouchableOpacity onPress={showModal} testID={testID} accessible accessibilityLabel="Ajuda">
        <MaterialCommunityIcons
          name="help-circle-outline"
          size={iconSize}
          color={iconColorValue}
        />
      </TouchableOpacity>

      <Portal>
        <Modal
          visible={visible}
          onDismiss={hideModal}
          contentContainerStyle={[
            styles.modalContent,
            { backgroundColor: theme.colors.surface },
          ]}
          testID={`${testID}-modal`}
        >
          <Surface style={styles.surface} elevation={4}>
            {/* Header */}
            <View style={styles.header}>
              <MaterialCommunityIcons
                name="help-circle"
                size={24}
                color={theme.colors.primary}
                style={styles.headerIcon}
              />
              <Text variant="titleLarge" style={styles.title}>
                {helpContent.title}
              </Text>
              <IconButton
                icon="close"
                size={24}
                onPress={hideModal}
                testID={`${testID}-close`}
              />
            </View>

            {/* Content */}
            <View style={styles.content}>
              <Text variant="bodyMedium" style={styles.description}>
                {helpContent.description}
              </Text>
            </View>

            {/* Footer (optional) */}
            {Platform.OS === 'web' && (
              <View style={styles.footer}>
                <Text variant="bodySmall" style={[styles.footerText, { color: theme.colors.outline }]}>
                  Precisa de mais ajuda? Contacte suporte@alldrivers.pt
                </Text>
              </View>
            )}
          </Surface>
        </Modal>
      </Portal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  absoluteTopRight: {
    position: 'absolute',
    top: 8,
    right: 8,
    zIndex: 1000,
  },
  modalContent: {
    marginHorizontal: 20,
    maxWidth: Platform.OS === 'web' ? 500 : undefined,
    alignSelf: 'center',
    borderRadius: 12,
  },
  surface: {
    borderRadius: 12,
    overflow: 'hidden',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0,0,0,0.1)',
  },
  headerIcon: {
    marginRight: 8,
  },
  title: {
    flex: 1,
    fontWeight: '600',
  },
  content: {
    padding: 20,
  },
  description: {
    lineHeight: 22,
  },
  footer: {
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderTopWidth: 1,
    borderTopColor: 'rgba(0,0,0,0.1)',
  },
  footerText: {
    textAlign: 'center',
    fontStyle: 'italic',
  },
});

